import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { ChartLegend, colorStyle, createValueFormatter, defaultChartColor, } from '../../../internal/charts/index.js';
import { formatShare } from '../../../internal/format-number.js';
import { ChartFrame } from '../chart-frame/index.js';
const GAP = 2;
/**
 * A donut of parts of one whole, inside ChartFrame: palette slices, a total
 * in the middle, a legend of values and shares, folding into "Other", and
 * the data table ("View as table") with every segment.
 */
export const DonutChart = forwardRef(function DonutChart({ segments, sort = 'desc', maxSegments = 6, size = 160, thickness: thicknessProp, centerValue, centerLabel, legendContent = 'both', showLegend = true, locale, valueFormat, formatValue, percentDigits = 0, summary, labels, status = 'ready', ...frame }, ref) {
    const words = {
        other: labels?.other ?? 'Other',
        segment: labels?.segment ?? 'Segment',
        value: labels?.value ?? 'Value',
        share: labels?.share ?? 'Share',
        total: labels?.total ?? 'Total',
    };
    const fmt = createValueFormatter(locale, valueFormat, formatValue);
    const clean = segments.map((s) => ({
        ...s,
        value: Number.isFinite(s.value) && s.value > 0 ? s.value : 0,
    }));
    const ordered = sort === 'desc'
        ? clean
            .map((s, i) => ({ s, i }))
            .sort((a, b) => b.s.value - a.s.value || a.i - b.i)
            .map(({ s }) => s)
        : clean;
    const total = ordered.reduce((t, s) => t + s.value, 0);
    const share = (v) => formatShare(v, total, {
        ...(locale !== undefined && { locale }),
        maximumFractionDigits: percentDigits,
    });
    const colored = ordered.map((s, i) => ({ ...s, color: s.color ?? defaultChartColor(i) }));
    const limit = Math.max(2, Math.floor(maxSegments));
    const slices = colored.length > limit
        ? [
            ...colored.slice(0, limit - 1),
            {
                key: '__other',
                label: words.other,
                value: colored.slice(limit - 1).reduce((t, s) => t + s.value, 0),
                color: 'neutral',
            },
        ]
        : colored;
    const drawn = slices.filter((s) => s.value > 0);
    const thickness = Math.min(size / 2, Math.max(4, thicknessProp ?? Math.round(size * 0.22)));
    const r = (size - thickness) / 2;
    const c = 2 * Math.PI * r;
    let start = 0;
    const arcs = drawn.map((s) => {
        const len = (s.value / total) * c;
        const gap = drawn.length > 1 ? GAP : 0;
        // Tiny shares keep a visible sliver instead of vanishing into the gap.
        const dash = Math.max(len - gap, Math.min(len, 1.5));
        const arc = { ...s, dash, offset: start };
        start += len;
        return arc;
    });
    const describe = (name, v) => {
        if (legendContent === 'none')
            return name;
        if (legendContent === 'value')
            return `${name} ${fmt(v)}`;
        if (legendContent === 'percent')
            return `${name} ${share(v)}`;
        return `${name} ${fmt(v)} · ${share(v)}`;
    };
    const generated = `${words.total} ${fmt(total)}. ${ordered
        .filter((s) => s.value > 0)
        .map((s) => `${s.label} ${fmt(s.value)} (${share(s.value)})`)
        .join(', ')}`;
    const table = {
        columns: [
            { key: 'label', label: words.segment },
            { key: 'value', label: words.value },
            { key: 'share', label: words.share },
        ],
        rows: [
            ...ordered.map((s) => ({ label: s.label, value: fmt(s.value), share: share(s.value) })),
            { label: words.total, value: fmt(total), share: share(total) },
        ],
    };
    const resolvedStatus = status === 'ready' && !(total > 0) ? 'empty' : status;
    const center = centerValue === undefined ? fmt(total) : centerValue;
    return (_jsx(ChartFrame, { ref: ref, ...frame, status: resolvedStatus, height: size, table: table, labels: labels, "data-chart": "donut", children: _jsxs("div", { className: "mn-donut-chart", "data-legend": showLegend ? '' : undefined, children: [_jsxs("div", { className: "mn-chart__plot mn-donut-chart__plot", role: "img", "aria-label": summary ?? generated, style: { width: size, height: size }, children: [_jsxs("svg", { className: "mn-donut-chart__svg", viewBox: `0 0 ${size} ${size}`, width: size, height: size, "aria-hidden": "true", focusable: "false", children: [_jsx("circle", { className: "mn-donut-chart__track", cx: size / 2, cy: size / 2, r: r, strokeWidth: thickness }), _jsx("g", { transform: `rotate(-90 ${size / 2} ${size / 2})`, children: arcs.map((a) => (_jsx("circle", { className: "mn-donut-chart__segment", cx: size / 2, cy: size / 2, r: r, strokeWidth: thickness, strokeDasharray: `${a.dash} ${c - a.dash}`, strokeDashoffset: -a.offset, style: colorStyle(a.color), children: _jsx("title", { children: `${a.label}: ${fmt(a.value)} (${share(a.value)})` }) }, a.key))) })] }), center !== null || centerLabel ? (_jsxs("div", { className: "mn-donut-chart__center", "aria-hidden": "true", children: [center !== null ? _jsx("span", { className: "mn-donut-chart__value", children: center }) : null, centerLabel ? _jsx("span", { className: "mn-donut-chart__label", children: centerLabel }) : null] })) : null] }), showLegend ? (_jsx(ChartLegend, { items: slices
                        .filter((s) => s.value > 0)
                        .map((s) => ({ key: s.key, label: describe(s.label, s.value), color: s.color })), marker: "square", label: labels?.legend ?? 'Legend', className: "mn-donut-chart__legend" })) : null] }) }));
});
//# sourceMappingURL=donut-chart.js.map